import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {DasUBoot} from "../DasUBoot";
import {DasUBootGameMode} from "../server/DasUBootGameMode";
import {WinConfig, WinConfigUtil} from "../utils/WinConfigUtil";
import {SymbolUtils} from "../utils/SymbolUtils";
import {DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DasUBootSound} from "../sound/DasUBootSound";

/**
 * Created by jonas on 2021-03-29.
 */

export class CombinedWin implements SimplePresentation {



    private getAllConfigsForLevel(levelName:string, winConfigs:WinConfig[]){
        const result = [];
        for (let conf of winConfigs){
            if (conf.name == levelName){
                result.push(conf);
            }
        }
        return result;
    }

    start(controller:SimplePresentationRunnerController):void {

        const tl:gsap.core.Timeline = gsap.timeline({onComplete:() => controller.finish()});
        const isBigWin = DasUBoot.model.winRatio >= 15;
        //const isBigWin = DasUBoot.model.winRatio >= 18; //This, takes the 20% rule into account. So it wont try to do bigwin, if it's not at least 20% over the limit.

        const belowStakeWinRestriction = DasUBoot.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && DasUBoot.model.winRatio <= 1;

        //-----------DATA STUFF
        const multiplier = Math.max(DasUBoot.model.clientData.multipliers.total , 1);
        let winConfigs:WinConfig[] = [];
        for (let i:number = 1; i <= multiplier; i++){
            const startRatio = DasUBoot.model.winRatioNonMultiplied * (i-1);
            const endRatio = DasUBoot.model.winRatioNonMultiplied * i;

            if (isBigWin){
                winConfigs = winConfigs.concat(WinConfigUtil.getBigWinLevels(startRatio,endRatio,i,i == multiplier));
            }else {
                winConfigs = winConfigs.concat(WinConfigUtil.getNormalWinLevels(startRatio,endRatio,i,i == multiplier));
            }
        }


        const lastLevelName = winConfigs[winConfigs.length - 1].name;
        if (isBigWin){
            //This makes sure that the last bigwin level is played in full duration but only to the win amount. With the multipliers spaced out based on the multiplier ratio.
            let lastLevelConfigs = this.getAllConfigsForLevel(lastLevelName,winConfigs);
            for (let config of lastLevelConfigs){
                config.durationRatio = config.calculateDurationNew(lastLevelConfigs[0].fromRatio, lastLevelConfigs[lastLevelConfigs.length-1].toRatio);
            }
        }


        const lastLevelConfig = winConfigs[winConfigs.length - 1].clone();
        if (lastLevelConfig.sound?.init && !belowStakeWinRestriction){
            const soundName = lastLevelConfig.sound?.init!;
            tl.add(()=>DasUBoot.soundPlayer.playSound(soundName));
        }

        tl.add(this.getInitialWin(isBigWin && !DasUBoot.model.winCapReached));

        if (DasUBoot.model.winCapReached){
            return;
        }

        if (!isBigWin){
            //Normal wins:
            tl.add(() => controller.setSkipCallback(() => tl.seek("ending", false)),0);
            const firstNormal = winConfigs[0];
            const lastNormal = winConfigs[winConfigs.length-1];
            const normalWinTl = gsap.timeline();
            const skipCountSound = lastLevelConfig.name == "TINY" || belowStakeWinRestriction;

            normalWinTl.add(DasUBoot.winView.getWinCountUp(firstNormal.fromRatio * DasUBoot.model.gameData.playedBetValue, firstNormal.toRatio * DasUBoot.model.gameData.playedBetValue, lastNormal.duration, 0, undefined, skipCountSound));
            for (let i:number = 1; i < winConfigs.length; i++){
                if (i == 1){
                    normalWinTl.add(DasUBoot.winView.getWinCountUpEnd(firstNormal.toRatio * DasUBoot.model.gameData.playedBetValue, 0.2));
                }
                const finalMultiplier = (i == winConfigs.length-1);
                normalWinTl.add(DasUBoot.winView.getMultiplier(winConfigs[i].multiplier, finalMultiplier, 0.2,finalMultiplier));
                normalWinTl.add(DasUBoot.winView.getWinCountUpEnd(winConfigs[i].toRatio * DasUBoot.model.gameData.playedBetValue, 0.2));
            }

            const timeDiff = Math.max(tl.duration() - normalWinTl.duration(), 0);
            tl.add(normalWinTl, timeDiff);

        } else {

            let prevConf;
            while (winConfigs.length > 0) {
                const config:WinConfig = winConfigs.shift()!;

                if (prevConf && prevConf.multiplier == config.multiplier){
                    config.newMultiplier = false;
                }

                const labelName = config.name + "_x"+ config.multiplier;

                tl.add(() => controller.setSkipCallback(() => tl.seek(labelName, false)));
                tl.add(this.getBigWinLevel(config, DasUBoot.model.gameData.playedBetValue, winConfigs.length == 0));
                tl.addLabel(labelName);
                prevConf = config;
            }
        }


        //Countup scale
        tl.add(DasUBoot.winView.getCountUpScaleAnimation(0,DasUBoot.model.gameData.totalSpinWinnings, tl.duration(), 0.4,lastLevelConfig.textScale),0);


        tl.addLabel("ending");

        const useTotalWin = DasUBoot.model.gameData.accumulatedRoundWin != DasUBoot.model.gameData.totalSpinWinnings || DasUBoot.model.data.mode != DasUBootGameMode.NORMAL;
        const stayDuration = isBigWin ? 2 : 1;

        tl.add(() => controller.setSkipCallback());
        tl.add(() => SlotGame.keypadPlugin.setWin(DasUBoot.model.gameData.accumulatedRoundWin, useTotalWin, DasUBoot.isWinBelowStake()));


        const endSound = lastLevelConfig.sound?.end;
        if (endSound && !belowStakeWinRestriction){
            tl.add(()=>DasUBoot.soundPlayer.playSound(endSound));
        }

        tl.add([
            ()=>DasUBoot.soundPlayer.stopBigWinMusic(),
            DasUBoot.winView.getBigWinEnd(stayDuration),
            DasUBoot.winView.getWinCountUpEnd(DasUBoot.model.gameData.totalSpinWinnings, stayDuration, DasUBoot.model.totalWays),
            ()=>DasUBoot.soundPlayer.fadeInMusic(1000)
        ]);


        tl.add(() => {
            DasUBoot.winView.getWinCountUpFade(DasUBoot.model.gameData.totalSpinWinnings, stayDuration * 0.5);
        });

    }

    private getInitialWin(isBigWin:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        const winSymbols = SymbolUtils.getAllSymbolsWithStateByReel(DasUBootSymbolStates.WIN);
        const winSymbolsList = SymbolUtils.getAllSymbolsWithState(DasUBootSymbolStates.WIN);
        const noWinSymbols = SymbolUtils.getAllSymbolsWithState(DasUBootSymbolStates.NO_WIN);

        tl.add(SymbolUtils.getSymbolStateAnimations(noWinSymbols, DasUBootSymbolStates.NO_WIN), 0);

        for (let reel of winSymbols){
            tl.add(SymbolUtils.getSymbolStateAnimations(reel, DasUBootSymbolStates.WIN), "<0.05");
        }

        if (isBigWin){
            tl.add(SymbolUtils.getSymbolShakeAnimations(winSymbolsList, 0.938));
            tl.add(()=>DasUBoot.soundPlayer.playSound(DasUBootSound.BIG_WIN_INTRO), "<");
        }

        return tl;
    }

    private getBigWinLevel(bigWinConfig:WinConfig, playedBetValue:number, lastLevel:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        if (bigWinConfig.newMultiplier && bigWinConfig.multiplier > 1){
            tl.add(()=>{DasUBoot.winView.getMultiplier(bigWinConfig.multiplier, lastLevel, 0.2, true)});
            tl.add(DasUBoot.winView.getWinCountUpEnd(bigWinConfig.fromRatio * playedBetValue, 0.0));
        }

        const countUp = DasUBoot.winView.getWinCountUp(
            bigWinConfig.fromRatio * playedBetValue,
            bigWinConfig.toRatio * playedBetValue,
            bigWinConfig.duration * bigWinConfig.durationRatio,
            0,
            new PIXI.Point(360,400),
            true,
            true
        );

        tl.add(countUp);


        if (bigWinConfig.sound && bigWinConfig.sound.start){
            tl.add(() => {
                DasUBoot.soundPlayer.playWinSound(bigWinConfig.sound!.start!);
            }, 0)
        }


        if (bigWinConfig.isBigWin){
            tl.add(() => {
                DasUBoot.soundPlayer.startBigWinMusic();
                DasUBoot.soundPlayer.fadeOutMusic(300);
            }, 0)
        }

        if (bigWinConfig.anim) {
            tl.add(()=>{DasUBoot.winView.getBigWinAnimation(bigWinConfig.anim!, bigWinConfig.particleLevel)}, 0);
        }


        return tl;
    }

    shouldRun():boolean {
        return DasUBoot.model.winRatio > 0;
    }


    private getBigWin(tl:gsap.core.Timeline) {

    }

    private getNormalWin(tl:gsap.core.Timeline) {

    }
}